import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, alpha, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

/**
 * Modern glass theme for the CMS: compact type scale, soft (desaturated) colours,
 * translucent blurred surfaces, and gentle hover motion.
 */
export const buildCmsTheme = (mode) => {
    const dark = mode === 'dark';
    const primary = dark
        ? { main: '#8b9cf7', light: '#b4bffb', dark: '#6472d6', contrastText: '#0a0e1a' }
        : { main: '#4a5bb0', light: '#7f8ed0', dark: '#3d4c96', contrastText: '#ffffff' };

    const errorMain = dark ? '#f28b98' : '#d0485a';
    const successMain = dark ? '#5fd3a0' : '#1f9d6b';
    const warningMain = dark ? '#f2c26b' : '#c98a1b';
    const infoMain = dark ? '#7cc0f2' : '#2f83c6';
    const line = dark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)';
    const glass = dark ? 'rgba(255,255,255,0.045)' : 'rgba(255,255,255,0.72)';
    const solid = dark ? '#141a2c' : '#ffffff';
    const hoverBg = dark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)';

    const ambient = dark
        ? 'radial-gradient(900px 520px at 6% -8%, rgba(99,112,230,.22), transparent 60%), radial-gradient(800px 520px at 100% 4%, rgba(56,189,178,.11), transparent 60%), radial-gradient(700px 500px at 60% 110%, rgba(168,85,247,.10), transparent 60%), #0a0e1a'
        : 'radial-gradient(900px 520px at 6% -8%, rgba(99,112,230,.16), transparent 60%), radial-gradient(800px 520px at 100% 4%, rgba(56,189,178,.10), transparent 60%), #eef1f7';

    return createTheme({
        palette: {
            mode,
            primary,
            secondary: { main: dark ? '#5eead4' : '#0f9d8a' },
            success: { main: dark ? '#5fd3a0' : '#1f9d6b' },
            warning: { main: dark ? '#f2c26b' : '#c98a1b' },
            error: { main: dark ? '#f28b98' : '#d0485a' },
            info: { main: dark ? '#7cc0f2' : '#2f83c6' },
            background: { default: dark ? '#0a0e1a' : '#eef1f7', paper: glass },
            text: dark
                ? { primary: '#e6e9f5', secondary: 'rgba(230,233,245,0.62)', disabled: 'rgba(230,233,245,0.38)' }
                : { primary: '#0f172a', secondary: '#526075', disabled: '#94a3b8' },
            divider: line,
            action: { hover: hoverBg, selected: dark ? 'rgba(139,156,247,0.16)' : 'rgba(74,91,176,0.10)' }
        },
        shape: { borderRadius: 12 },
        typography: {
            fontFamily: FONT,
            fontSize: 13,
            h1: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em' },
            h2: { fontSize: '1.7rem', fontWeight: 700, letterSpacing: '-0.02em' },
            h3: { fontSize: '1.45rem', fontWeight: 700, letterSpacing: '-0.02em' },
            h4: { fontSize: '1.3rem', fontWeight: 700, letterSpacing: '-0.015em' },
            h5: { fontSize: '1.1rem', fontWeight: 650, letterSpacing: '-0.01em' },
            h6: { fontSize: '0.95rem', fontWeight: 650 },
            subtitle1: { fontSize: '0.9rem', fontWeight: 600 },
            subtitle2: { fontSize: '0.82rem', fontWeight: 600 },
            body1: { fontSize: '0.85rem' },
            body2: { fontSize: '0.79rem' },
            caption: { fontSize: '0.72rem' },
            button: { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' }
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: { background: ambient, backgroundAttachment: 'fixed', minHeight: '100vh' },
                    // Stack's sibling margin cancels a Grid container's negative margin, which pushes
                    // grids inside a Stack off-centre. Restore the offset for each spacing level.
                    ...Object.fromEntries([1, 2, 3, 4].map((n) => [
                        `.MuiStack-root > .MuiGrid-container.MuiGrid-spacing-xs-${n}`,
                        { marginLeft: `-${n * 8}px !important`, marginTop: '0 !important' }
                    ])),
                    '*::-webkit-scrollbar': { width: 10, height: 10 },
                    '*::-webkit-scrollbar-thumb': { background: dark ? 'rgba(255,255,255,0.14)' : 'rgba(15,23,42,0.18)', borderRadius: 8, border: '2px solid transparent', backgroundClip: 'content-box' }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backdropFilter: 'blur(18px) saturate(150%)',
                        WebkitBackdropFilter: 'blur(18px) saturate(150%)',
                        border: `1px solid ${line}`,
                        boxShadow: dark ? 'none' : '0 1px 2px rgba(15,23,42,0.04), 0 8px 30px -18px rgba(15,23,42,0.18)',
                        transition: 'border-color .2s ease, box-shadow .2s ease, transform .2s ease, background-color .2s ease'
                    },
                    rounded: { borderRadius: 16 }
                }
            },
            MuiCard: { styleOverrides: { root: { borderRadius: 16 } } },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: dark ? 'rgba(10,14,26,0.6)' : 'rgba(255,255,255,0.7)',
                        backdropFilter: 'blur(20px) saturate(160%)',
                        WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                        border: 0,
                        borderBottom: `1px solid ${line}`,
                        boxShadow: 'none'
                    }
                }
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundImage: 'none',
                        backgroundColor: dark ? 'rgba(12,16,30,0.72)' : 'rgba(255,255,255,0.75)',
                        backdropFilter: 'blur(22px) saturate(160%)',
                        WebkitBackdropFilter: 'blur(22px) saturate(160%)',
                        borderRight: `1px solid ${line}`,
                        borderRadius: 0
                    }
                }
            },
            // Floating layers stay near-opaque so text remains readable over content
            MuiPopover: { styleOverrides: { paper: { backgroundColor: solid, backgroundImage: 'none', borderRadius: 12 } } },
            MuiMenu: { styleOverrides: { paper: { backgroundColor: solid, borderRadius: 12 } } },
            MuiDialog: { styleOverrides: { paper: { backgroundColor: solid, borderRadius: 20 } } },
            MuiAutocomplete: { styleOverrides: { paper: { backgroundColor: solid } } },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: { borderRadius: 10, padding: '6px 14px', transition: 'all .18s ease' },
                    containedPrimary: {
                        boxShadow: `0 4px 14px -4px ${dark ? 'rgba(139,156,247,0.55)' : 'rgba(74,91,176,0.45)'}`,
                        '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 8px 20px -6px ${dark ? 'rgba(139,156,247,0.7)' : 'rgba(74,91,176,0.55)'}` }
                    },
                    outlined: { borderColor: line, '&:hover': { borderColor: primary.main, background: hoverBg } }
                }
            },
            MuiIconButton: {
                defaultProps: { size: 'small' },
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        padding: 7,
                        transition: 'all .18s ease',
                        '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
                        '&:hover': { backgroundColor: hoverBg, transform: 'translateY(-1px)' }
                    },
                    // Semantic action buttons (edit / delete / view...) share one tinted style
                    colorPrimary: { color: primary.main, backgroundColor: alpha(primary.main, 0.1), '&:hover': { backgroundColor: alpha(primary.main, 0.2), boxShadow: `0 6px 16px -6px ${alpha(primary.main, 0.7)}` } },
                    colorError: { color: errorMain, backgroundColor: alpha(errorMain, 0.1), '&:hover': { backgroundColor: alpha(errorMain, 0.2), boxShadow: `0 6px 16px -6px ${alpha(errorMain, 0.7)}` } },
                    colorSuccess: { color: successMain, backgroundColor: alpha(successMain, 0.1), '&:hover': { backgroundColor: alpha(successMain, 0.2), boxShadow: `0 6px 16px -6px ${alpha(successMain, 0.7)}` } },
                    colorWarning: { color: warningMain, backgroundColor: alpha(warningMain, 0.1), '&:hover': { backgroundColor: alpha(warningMain, 0.2), boxShadow: `0 6px 16px -6px ${alpha(warningMain, 0.7)}` } },
                    colorInfo: { color: infoMain, backgroundColor: alpha(infoMain, 0.1), '&:hover': { backgroundColor: alpha(infoMain, 0.2) } }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 8, fontWeight: 600, height: 24 },
                    outlined: { borderColor: line },
                    // Solid chips become soft tinted "glow" chips
                    filledSuccess: { backgroundColor: alpha(successMain, 0.16), color: successMain, border: `1px solid ${alpha(successMain, 0.3)}` },
                    filledError: { backgroundColor: alpha(errorMain, 0.16), color: errorMain, border: `1px solid ${alpha(errorMain, 0.3)}` },
                    filledWarning: { backgroundColor: alpha(warningMain, 0.16), color: warningMain, border: `1px solid ${alpha(warningMain, 0.3)}` },
                    filledInfo: { backgroundColor: alpha(infoMain, 0.16), color: infoMain, border: `1px solid ${alpha(infoMain, 0.3)}` },
                    filledPrimary: { backgroundColor: alpha(primary.main, 0.16), color: primary.main, border: `1px solid ${alpha(primary.main, 0.3)}` }
                }
            },
            MuiTabs: {
                styleOverrides: {
                    root: { minHeight: 40 },
                    indicator: { height: 2, borderRadius: 2, background: `linear-gradient(90deg, ${primary.main}, ${primary.light})` }
                }
            },
            MuiTab: { styleOverrides: { root: { minHeight: 40, padding: '8px 16px', fontSize: '0.82rem', textTransform: 'none' } } },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        backgroundColor: dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: line },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dark ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.25)' },
                        '&.Mui-focused': { boxShadow: `0 0 0 3px ${dark ? 'rgba(139,156,247,0.18)' : 'rgba(74,91,176,0.14)'}` },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primary.main, borderWidth: 1 }
                    },
                    input: { padding: '9px 12px', backgroundColor: 'transparent', borderRadius: 'inherit' }
                }
            },
            // Rows are separate glass "pills" instead of one ruled grid
            MuiTable: { styleOverrides: { root: { borderCollapse: 'separate', borderSpacing: '0 8px' } } },
            MuiTableHead: {
                styleOverrides: { root: { '& .MuiTableCell-root': { borderBottom: 0, paddingBottom: 2, paddingTop: 2 } } }
            },
            MuiTableBody: {
                styleOverrides: {
                    root: {
                        '& > .MuiTableRow-root > .MuiTableCell-root': {
                            backgroundColor: dark ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.75)',
                            borderTop: `1px solid ${line}`,
                            borderBottom: `1px solid ${line}`,
                            backdropFilter: 'blur(12px)',
                            transition: 'background-color .18s ease, border-color .18s ease'
                        },
                        '& > .MuiTableRow-root > .MuiTableCell-root:first-of-type': { borderLeft: `1px solid ${line}`, borderRadius: '12px 0 0 12px' },
                        '& > .MuiTableRow-root > .MuiTableCell-root:last-of-type': { borderRight: `1px solid ${line}`, borderRadius: '0 12px 12px 0' },
                        '& > .MuiTableRow-root:hover > .MuiTableCell-root': {
                            backgroundColor: dark ? 'rgba(139,156,247,0.09)' : 'rgba(74,91,176,0.06)',
                            borderColor: alpha(primary.main, 0.35)
                        }
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { padding: '10px 14px', borderColor: line, fontSize: '0.8rem' },
                    head: { fontWeight: 650, fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: dark ? 'rgba(230,233,245,0.55)' : '#64748b' }
                }
            },
            MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: 'transparent' } } } },
            MuiListItemButton: { styleOverrides: { root: { borderRadius: 10, transition: 'all .18s ease' } } },
            MuiLinearProgress: {
                styleOverrides: {
                    root: { borderRadius: 8, height: 6, backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)' },
                    bar: { borderRadius: 8, background: `linear-gradient(90deg, ${primary.dark}, ${primary.main}, ${primary.light})` }
                }
            },
            MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 8, fontSize: 12, backgroundColor: dark ? '#252d45' : '#0f172a' } } },
            MuiDivider: { styleOverrides: { root: { borderColor: line } } }
        }
    });
};

const THEME_STORAGE_KEY = 'cms_theme_mode';

export const useThemeMode = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeMode must be used within ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    const [mode, setMode] = useState(() => {
        // Get theme from localStorage or default to 'light'
        const savedMode = localStorage.getItem(THEME_STORAGE_KEY);
        return savedMode || 'light';
    });

    useEffect(() => {
        // Save theme preference to localStorage
        localStorage.setItem(THEME_STORAGE_KEY, mode);
    }, [mode]);

    useEffect(() => {
        // Reflect theme mode on the document body for global (non-MUI) styles
        if (typeof document !== 'undefined') {
            document.body.classList.toggle('theme-dark', mode === 'dark');
            document.body.classList.toggle('theme-light', mode === 'light');
        }
    }, [mode]);

    const toggleTheme = () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    };

    const theme = useMemo(() => buildCmsTheme(mode), [mode]);

    const value = {
        mode,
        toggleTheme,
        theme,
    };

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

